import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMarketRealtime } from '@/lib/realtime';
import { getPlaeeOrderBookGetOrderBookQueryKey } from '@/lib/endpoints';
import type { PlaeeOrderBookEntryDto, PlaeeOrderBookResponseDto } from '@/lib/models';
import { CROSSED_RESEED_DELAY_MS, RESEED_COOLDOWN_MS, isCrossedSpread, sanitizeSpread } from './order-book-sync';

/**
 * Merged order-book view in the snapshot's **dollar** price units (0–1), ready
 * to render. Asks are ascending (best/lowest first), bids descending
 * (best/highest first) — the same convention Plaee's snapshot already returns.
 */
export interface LiveOrderBookView {
  asks: PlaeeOrderBookEntryDto[];
  bids: PlaeeOrderBookEntryDto[];
  /**
   * Spread in dollars (bestAsk − bestBid), recomputed from the live depth.
   * Never negative: a crossed book renders as unknown while the hook re-seeds
   * the mirror from the snapshot.
   */
  spread: number | undefined;
  /** Last traded price in dollars — from the snapshot (book events carry none). */
  lastPrice: number | undefined;
}

/** Depth derived from the live book (dollar prices), held in render state. */
interface LiveDepth {
  asks: PlaeeOrderBookEntryDto[];
  bids: PlaeeOrderBookEntryDto[];
  spread: number | undefined;
}

/** Dollars (0–1) → whole-cent key, the unit the realtime contract uses. */
function toCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/** A live book side kept as `cents → quantity` so price keys compare exactly. */
type BookSide = Map<number, number>;

/** Sort a side and project it back to dollar-priced render entries. */
function project(side: BookSide, dir: 'asc' | 'desc'): PlaeeOrderBookEntryDto[] {
  return [...side.entries()]
    .filter(([, quantity]) => quantity > 0)
    .sort((a, b) => (dir === 'asc' ? a[0] - b[0] : b[0] - a[0]))
    .map(([cents, quantity]) => ({ price: cents / 100, quantity }));
}

/** Project both sides into the dollar-priced depth, with a derived spread. */
function deriveDepth(bids: BookSide, asks: BookSide): LiveDepth {
  const asksOut = project(asks, 'asc');
  const bidsOut = project(bids, 'desc');
  const bestAsk = asksOut[0]?.price;
  const bestBid = bidsOut[0]?.price;
  const spread = bestAsk != null && bestBid != null ? bestAsk - bestBid : undefined;
  return { asks: asksOut, bids: bidsOut, spread };
}

/**
 * Overlay live depth onto an order-book snapshot.
 *
 * Seeds from the `/plaee/orderbook/:outcomeId` snapshot, then applies the market
 * Centrifugo `OrderBookUpdated` stream ({@link useMarketRealtime}) so the book
 * stays current without refetching:
 * - `isFull` events replace the side wholesale (also the snapshot of record);
 * - incremental events apply each `upsert`/`delete` (a 0-quantity upsert prunes
 *   the level).
 *
 * Prices on the wire are whole cents; the snapshot is dollars — both are
 * normalised to cents internally and projected back to dollars on the way out.
 * The 50+/sec event rate is coalesced to one re-render per animation frame; the
 * derived depth lives in state so render never reads the mutable book directly.
 *
 * Returns `undefined` when there is no snapshot yet, or when the market ids
 * needed to resolve the realtime channel are absent (live overlay disabled —
 * the caller should fall back to the raw snapshot).
 */
export function useLiveOrderBook(
  snapshot: PlaeeOrderBookResponseDto | undefined,
  outcomeId: string,
  marketId: string | null | undefined,
  externalMarketId: string | null | undefined,
): LiveOrderBookView | undefined {
  const queryClient = useQueryClient();
  const bidsRef = useRef<BookSide>(new Map());
  const asksRef = useRef<BookSide>(new Map());
  // True once a base (snapshot or full event) exists — incremental updates are
  // meaningless without one, so we drop them until then.
  const hasBaseRef = useRef(false);
  const frameRef = useRef<number | null>(null);
  const lastReseedAtRef = useRef(Number.NEGATIVE_INFINITY);
  const [depth, setDepth] = useState<LiveDepth | null>(null);

  // Push the current book into render state. Only ever called from the seed
  // layout-effect or the coalescing frame — never during render.
  function flush() {
    setDepth(deriveDepth(bidsRef.current, asksRef.current));
  }

  function scheduleFlush() {
    if (frameRef.current != null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      flush();
    });
  }

  // Seed (and re-seed on snapshot refetch / outcome change) from the REST
  // snapshot before paint, so the live ids never show a snapshot→sorted flash.
  // A later full event re-bases on top of this. When the outcome changes and its
  // snapshot has not loaded yet we reset to an empty, base-less book, so the
  // prior outcome's levels never leak into the new one's incremental updates.
  useLayoutEffect(() => {
    if (snapshot) {
      bidsRef.current = new Map(snapshot.bids.map((e) => [toCents(e.price), e.quantity]));
      asksRef.current = new Map(snapshot.asks.map((e) => [toCents(e.price), e.quantity]));
      hasBaseRef.current = true;
    } else {
      bidsRef.current = new Map();
      asksRef.current = new Map();
      hasBaseRef.current = false;
    }
    flush();
  }, [snapshot, outcomeId]);

  // Cancel any pending frame on unmount.
  useLayoutEffect(
    () => () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  // Self-heal a crossed book. A sustained negative spread means the local
  // mirror drifted (e.g. deletes missed across a reconnect) — a one-frame
  // crossing mid-trade self-corrects, so wait CROSSED_RESEED_DELAY_MS before
  // invalidating the snapshot query; the refetch re-seeds the mirror above.
  // The cooldown keeps a flapping (or genuinely crossed upstream) book from
  // hammering the API. If the crossing persists, `crossed` stays true and this
  // effect does not re-fire — no refetch loop.
  const crossed = isCrossedSpread(depth?.spread);
  useEffect(() => {
    if (!crossed) return;
    const timer = setTimeout(() => {
      const now = Date.now();
      if (now - lastReseedAtRef.current < RESEED_COOLDOWN_MS) return;
      lastReseedAtRef.current = now;
      void queryClient.invalidateQueries({ queryKey: getPlaeeOrderBookGetOrderBookQueryKey(outcomeId) });
    }, CROSSED_RESEED_DELAY_MS);
    return () => clearTimeout(timer);
  }, [crossed, outcomeId, queryClient]);

  useMarketRealtime(marketId, externalMarketId, {
    onOrderBookUpdated: (e) => {
      // The market channel carries every outcome; keep only this one's book.
      if (e.outcomeId !== outcomeId) return;

      if (e.isFull) {
        bidsRef.current = new Map(e.bids.map((l) => [l.price, l.quantity]));
        asksRef.current = new Map(e.asks.map((l) => [l.price, l.quantity]));
        hasBaseRef.current = true;
      } else if (e.updates?.length) {
        if (!hasBaseRef.current) return;
        for (const u of e.updates) {
          const side = u.side === 'bid' ? bidsRef.current : asksRef.current;
          if (u.action === 'delete' || u.quantity <= 0) side.delete(u.price);
          else side.set(u.price, u.quantity);
        }
      } else {
        return;
      }
      scheduleFlush();
    },
  });

  if (!snapshot) return undefined;

  // Before the first flush (and when no market ids gate the live stream) derive
  // the book straight from the snapshot, so the order is normalised from the
  // very first paint regardless of how Plaee ordered the snapshot.
  const base =
    depth ??
    deriveDepth(
      new Map(snapshot.bids.map((e) => [toCents(e.price), e.quantity])),
      new Map(snapshot.asks.map((e) => [toCents(e.price), e.quantity])),
    );

  return {
    asks: base.asks,
    bids: base.bids,
    // Never render a negative spread — a crossed book is a sync artifact (the
    // effect above re-seeds it), not a price a user should see.
    spread: sanitizeSpread(base.spread ?? snapshot.spread),
    lastPrice: snapshot.lastPrice,
  };
}
